import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Lightbulb } from 'lucide-react';

const CATEGORIES = ['outdoors', 'crafts', 'cooking', 'painting', 'social', 'exercise', 'learning', 'entertainment', 'relaxation'];
const TIME_OPTIONS = ['5 mins', '15 mins', '30 mins', '1 hr', '1-2 hrs', '2+ hrs'];

const SubmitIdeaPage = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({ title: '', description: '', category: '', time_needed: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.description || !formData.category || !formData.time_needed) {
      toast.error('Please fill in all fields');
      return;
    }
    setIsLoading(true);
    try {
      await axios.post('/ideas', formData);
      toast.success('Idea posted!');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to post idea');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f7f9fa]">
      <header className="sticky top-0 z-50 bg-white border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-4 h-14 flex items-center gap-4">
          <Button
            data-testid="back-btn"
            onClick={() => navigate('/')}
            variant="ghost"
            size="sm"
            className="rounded-full"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-lg font-bold text-gray-900">Create Post</h1>
        </div>
      </header>

      <main className="max-w-3xl mx-auto p-4">
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="title" className="text-sm font-medium text-gray-900">Title</Label>
              <Input
                id="title"
                data-testid="idea-title-input"
                placeholder="What's your idea?"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                className="text-base h-11 border-gray-300 rounded-lg"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description" className="text-sm font-medium text-gray-900">Description</Label>
              <Textarea
                id="description"
                data-testid="idea-description-input"
                placeholder="Share more details..."
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
                rows={6}
                className="text-base border-gray-300 rounded-lg resize-none"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="category-select" className="text-sm font-medium text-gray-900">Category</Label>
                <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
                  <SelectTrigger data-testid="category-select" id="category-select" className="h-11 border-gray-300 rounded-lg">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent className="z-[100]">
                    {CATEGORIES.map((cat) => (
                      <SelectItem key={cat} value={cat} data-testid={`category-option-${cat}`}>
                        {cat.charAt(0).toUpperCase() + cat.slice(1)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="time-select" className="text-sm font-medium text-gray-900">Time Needed</Label>
                <Select value={formData.time_needed} onValueChange={(value) => setFormData({ ...formData, time_needed: value })}>
                  <SelectTrigger data-testid="time-select" id="time-select" className="h-11 border-gray-300 rounded-lg">
                    <SelectValue placeholder="Select time" />
                  </SelectTrigger>
                  <SelectContent className="z-[100]">
                    {TIME_OPTIONS.map((time) => (
                      <SelectItem key={time} value={time} data-testid={`time-option-${time}`}>
                        {time}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                onClick={() => navigate('/')}
                variant="outline"
                className="flex-1 h-11 rounded-lg border-gray-300"
              >
                Cancel
              </Button>
              <Button
                data-testid="submit-idea-form-btn"
                type="submit"
                className="flex-1 h-11 bg-[#1d9bf0] hover:bg-[#1a8cd8] text-white font-semibold rounded-lg"
                disabled={isLoading}
              >
                {isLoading ? 'Posting...' : 'Post'}
              </Button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
};

export default SubmitIdeaPage;